import { gql, useLazyQuery } from '@apollo/client';
import type { CurrencyCodes } from '@generated/graphql';
import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_EXCHANGE_RATE,
  FEE_BASE_CURRENCY,
} from './withdrawal.constants';

export const FETCH_FOREIGN_EXCHANGE_RATE = gql`
  query FetchForeignExchangeRate($input: FetchForeignExchangeRateInput!) {
    fetchForeignExchangeRate(input: $input) {
      rate
    }
  }
`;

type UseExchangeRateOptions = {
  organizationId?: string;
  targetCurrency?: CurrencyCodes;
  baseCurrency?: CurrencyCodes;
};

type UseExchangeRateReturn = {
  /** The exchange rate from baseCurrency to targetCurrency */
  rate: number;
  /** Whether the rate is currently being fetched */
  isLoading: boolean;
  /** Whether the rate has been successfully loaded or defaulted */
  isReady: boolean;
  /** Whether we have a real fetched rate (not a fallback default) */
  hasRealRate: boolean;
  /** Convert an amount FROM target currency TO base currency */
  convertToBaseCurrency: (amountInTargetCurrency: number) => number;
  /** Convert an amount FROM base currency TO target currency */
  convertFromBaseCurrency: (amountInBaseCurrency: number) => number;
};

/**
 * Custom hook for fetching and managing exchange rates.
 *
 * Handles:
 * - Fetching rates from the API
 * - Caching rates and their inverses
 * - Graceful fallback to rate=1 on error
 * - Converting amounts between currencies
 */
export const useExchangeRate = ({
  organizationId,
  targetCurrency,
  baseCurrency = FEE_BASE_CURRENCY,
}: UseExchangeRateOptions): UseExchangeRateReturn => {
  const [rate, setRate] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasRealRate, setHasRealRate] = useState(false);
  const [cachedRates, setCachedRates] = useState<Map<string, number>>(
    () => new Map(),
  );

  const setDefaultRateIfNeeded = useCallback(() => {
    setRate((currentRate) =>
      currentRate === 0 ? DEFAULT_EXCHANGE_RATE : currentRate,
    );
  }, []);

  const [fetchExchangeRate] = useLazyQuery(FETCH_FOREIGN_EXCHANGE_RATE, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setIsLoading(false);
      const fetchedRate = data?.fetchForeignExchangeRate?.rate;

      if (fetchedRate) {
        // Cache the rate using the current baseCurrency and targetCurrency
        if (baseCurrency && targetCurrency && baseCurrency !== targetCurrency) {
          const inverseRate = 1 / fetchedRate;

          setCachedRates((prev) => {
            const next = new Map(prev);
            next.set(`${baseCurrency}-${targetCurrency}`, fetchedRate);
            next.set(`${targetCurrency}-${baseCurrency}`, inverseRate);
            return next;
          });
        }

        // Always update rate and hasRealRate when we get a real fetched rate
        setRate(fetchedRate);
        setHasRealRate(true);
      } else {
        // Rate not found in response, default to 1
        setDefaultRateIfNeeded();
      }
    },
    onError: () => {
      setIsLoading(false);
      // On error, default to rate = 1 so the logic continues
      setDefaultRateIfNeeded();
    },
  });

  const findCachedRate = useCallback(
    (from: CurrencyCodes, to: CurrencyCodes): number | undefined => {
      if (from === to) return DEFAULT_EXCHANGE_RATE;

      return cachedRates.get(`${from}-${to}`);
    },
    [cachedRates],
  );

  const fetchRateIfNeeded = useCallback(
    (from: CurrencyCodes, to: CurrencyCodes) => {
      // Same currency - rate is 1 and this IS a real/correct rate
      if (from === to) {
        setRate(DEFAULT_EXCHANGE_RATE);
        setHasRealRate(true);
        return;
      }

      // Different currencies but no organizationId - can't fetch, use default
      if (!organizationId) {
        setDefaultRateIfNeeded();
        return;
      }

      const existingRate = findCachedRate(from, to);

      if (existingRate !== undefined) {
        // Always update rate when we have a cached rate
        setRate(existingRate);
        setHasRealRate(true);
        return;
      }

      setIsLoading(true);

      fetchExchangeRate({
        variables: {
          input: {
            from,
            to,
            organizationId,
            inheritFromParents: true,
          },
        },
      });
    },
    [organizationId, findCachedRate, fetchExchangeRate, setDefaultRateIfNeeded],
  );

  // Fetch exchange rate when target currency changes
  useEffect(() => {
    if (!targetCurrency) {
      // No target currency specified
      setDefaultRateIfNeeded();
      return;
    }

    if (targetCurrency === baseCurrency) {
      // Same currency - rate is 1 and this IS the correct rate
      setRate(DEFAULT_EXCHANGE_RATE);
      setHasRealRate(true);
      return;
    }

    // Reset state when currency changes to trigger a new fetch
    setHasRealRate(false);
    fetchRateIfNeeded(baseCurrency, targetCurrency);
  }, [targetCurrency, baseCurrency, fetchRateIfNeeded, setDefaultRateIfNeeded]);

  const convertToBaseCurrency = useCallback(
    (amountInTargetCurrency: number): number => {
      if (rate <= 0) return amountInTargetCurrency;

      return Math.round(amountInTargetCurrency / rate);
    },
    [rate],
  );

  const convertFromBaseCurrency = useCallback(
    (amountInBaseCurrency: number): number => {
      if (rate <= 0) return amountInBaseCurrency;

      return Math.round(amountInBaseCurrency * rate);
    },
    [rate],
  );

  const isReady = rate > 0;

  return {
    rate,
    isLoading,
    isReady,
    hasRealRate,
    convertToBaseCurrency,
    convertFromBaseCurrency,
  };
};
